import { useEffect, useState, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import qs from 'querystring';

import * as api from '../../api';
import * as store from '../../reducers/store';
import * as rootReducer from '../../reducers/root';
import * as FormUtils from '../../utils/form';

import Loader from '../../components/Loader';
import { useTheme } from '../../hooks/common/useTheme';
import { InboundRouter } from '../inboundRouter';
import { useBookedSlot } from '../../hooks/schedule/useBookedSlot';
import { getProviderNameFull } from '../../utils/provider';
import { verifyRequest } from '../../utils';

interface StateProps extends Pick<store.Inbound, 'provisionalPatientId'> {
  selectedFormSetId: string | null;
  urlIdentifier: string | null;
  ehrProviderInformation: api.User.EhrProviderInformationPractice[] | null;
  selectedProviderId: string | null;
  formSets: store.FormSets;
}

interface DispatchProps {
  setFormSets: (
    formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
  ) => void;
  setSelectedFormSetId: (selectedFormSetId: string | null) => void;
  setSelectedFormId: (selectedFormId: string | null) => void;
  setLoggedInPatient: (patient: api.Patient.Patient) => void;
  setAuthenticationCode: (authenticationCode: string) => void;
  setEhrProviderInformationList: (
    ehrProviderInformation: api.User.EhrProviderInformationPractice[] | null,
  ) => void;
  setSelectedProviderId: (providerId: string | null) => void;
  setProvisionalPatientId: (provisionalPatientId: string) => void;
  setUrlIdentifier: (urlIdentifier: string | null) => void;
  setDocumentTitle: (documentTitle: string | null) => void;
  setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) => void;
  setIsProvisionalPatientReconciled: (
    isProvisionalPatientReconciled: boolean,
  ) => void;
}

interface Props extends DispatchProps, StateProps {
  updateDocumentTitle?: boolean;
}

const getMaybeSelectedEhrProviderWithPracticeFromQueryParams = (
  search: string,
  providers: api.User.EhrProviderInformationPractice[],
) => {
  const queryValues = qs.parse(search.slice(1));
  const { providerId: paramProviderId } = queryValues;
  if (typeof paramProviderId !== 'string') return;
  const selectedEhrProviderWithPractice = providers.find(
    (provider) => provider.providerUserId === paramProviderId,
  );
  if (!selectedEhrProviderWithPractice) return;
  return selectedEhrProviderWithPractice;
};

const Schedule: React.FC<Props> = ({
  setFormSets,
  setSelectedFormSetId,
  setSelectedFormId,
  setLoggedInPatient,
  setAuthenticationCode,
  setEhrProviderInformationList,
  setSelectedProviderId,
  setProvisionalPatientId,
  setDocumentTitle,
  setIsProvisionalPatientReconciled,
  selectedFormSetId,
  provisionalPatientId,
  setUrlIdentifier,
  urlIdentifier,
  setOnCaptchaSuccess,
  ehrProviderInformation,
  selectedProviderId,
  formSets,
  updateDocumentTitle = true,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { schedulingCode } = useParams<{
    schedulingCode: string;
  }>();
  const [isLoading, setIsLoading] = useState(true);
  const authCode = useSelector(
    (store: store.All) => store.authenticationCode ?? undefined,
  );
  const { isThemeLoading } = useTheme({ authCode, preventFallback: true });
  const { selectedEhrProviderWithPractice, selectedTimeSlot } = useBookedSlot();

  useEffect(() => {
    if (!selectedFormSetId || !formSets[selectedFormSetId]?.completed) return;
    const queryParams = new URLSearchParams(location.search.slice(1));
    queryParams.delete('page');
    history.push({
      pathname: `${FormUtils.getInboundPath(location.pathname)}/end`,
      search: queryParams.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormSetId, formSets]);

  const reduxSetup = useCallback(
    (provisionalPatientFormSet: api.Register.ProvisionalFormSetData) => {
      const selectedFormId = Object.keys(
        provisionalPatientFormSet.previsitFormSet.forms,
      )[0];
      setFormSets([
        {
          previsitFormSet: provisionalPatientFormSet.previsitFormSet,
          encounter: provisionalPatientFormSet.encounter,
        },
      ]);
      setSelectedFormSetId(provisionalPatientFormSet.previsitFormSet.id);
      setSelectedFormId(selectedFormId);
      setLoggedInPatient(provisionalPatientFormSet.patient);
      setAuthenticationCode(
        provisionalPatientFormSet.previsitFormSet.authenticationCode,
      );
      setEhrProviderInformationList(
        provisionalPatientFormSet.ehrProviderInformation,
      );
      setProvisionalPatientId(provisionalPatientFormSet.provisionalPatientId);
      if (
        !provisionalPatientFormSet.previsitFormSet.patientEhrId.startsWith(
          api.EhrInformation.EHR_GENERATED_PREFIX,
        )
      ) {
        setIsProvisionalPatientReconciled(true);
      }
    },
    // We don't want this callback to change when location.search changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setAuthenticationCode,
      setEhrProviderInformationList,
      setFormSets,
      setLoggedInPatient,
      setSelectedFormId,
      setSelectedFormSetId,
    ],
  );

  useEffect(() => {
    if (!ehrProviderInformation?.length) return;
    const selectedEhrProviderWithPractice =
      getMaybeSelectedEhrProviderWithPracticeFromQueryParams(
        location.search,
        ehrProviderInformation,
      );
    if (
      selectedEhrProviderWithPractice &&
      selectedEhrProviderWithPractice.providerUserId !== selectedProviderId
    ) {
      setSelectedProviderId(selectedEhrProviderWithPractice.providerUserId);
    }
  }, [
    location.search,
    ehrProviderInformation,
    setSelectedProviderId,
    selectedProviderId,
  ]);

  useEffect(() => {
    const resetSession = async () => {
      await api.client.Previsit.logout();
      store.resetStore();
    };

    if (!urlIdentifier) {
      setUrlIdentifier(schedulingCode);
    }
    if (urlIdentifier && urlIdentifier !== schedulingCode) {
      resetSession();
    }
  }, [schedulingCode, urlIdentifier, setUrlIdentifier]);

  useEffect(
    () => {
      const loadInitial = async () => {
        setIsLoading(true);
        const queryValues = qs.parse(location.search.slice(1));
        const { source: paramSource } = queryValues;
        const source =
          typeof paramSource === 'string' ? paramSource : undefined;
        await verifyRequest(
          () =>
            api.client.Previsit.findOrCreateProvisionalFormSet({
              domain: window.location.hostname,
              urlIdentifier: schedulingCode,
              source,
            }),
          async (
            provisionalPatientFormSet: api.Register.ProvisionalFormSetData,
          ) => {
            reduxSetup(provisionalPatientFormSet);
            setIsLoading(false);
          },
          setOnCaptchaSuccess,
        );
      };
      if (urlIdentifier !== schedulingCode) return;
      if (!selectedFormSetId) loadInitial();
    },
    // eslint-disable-next-line
    [
      schedulingCode,
      setEhrProviderInformationList,
      reduxSetup,
      selectedFormSetId,
      setSelectedFormSetId,
      urlIdentifier,
      setOnCaptchaSuccess,
    ],
  );

  useEffect(() => {
    if (!updateDocumentTitle) return;
    if (!selectedEhrProviderWithPractice) {
      setDocumentTitle('Scheduling');
      return;
    }
    const titleSuffix = ' - Scheduling';
    const fullProviderNameWithPrefix =
      selectedTimeSlot &&
      selectedTimeSlot.scheduleAppointmentDisplayBy ===
        api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
          .Practice
        ? selectedEhrProviderWithPractice.name
        : getProviderNameFull({ provider: selectedEhrProviderWithPractice });
    setDocumentTitle(fullProviderNameWithPrefix + titleSuffix);
  }, [
    selectedEhrProviderWithPractice,
    selectedTimeSlot,
    setDocumentTitle,
    updateDocumentTitle,
  ]);

  if (isLoading || isThemeLoading || !provisionalPatientId) {
    return <Loader />;
  }
  return <InboundRouter />;
};

export function mapStateToProps({
  formSets,
  inbound: {
    urlIdentifier,
    provisionalPatientId,
    ehrProviderInformation,
    selectedProviderId,
  },
  selectedFormSetId,
}: store.All): StateProps {
  return {
    formSets,
    selectedFormSetId,
    urlIdentifier,
    provisionalPatientId,
    ehrProviderInformation,
    selectedProviderId,
  };
}

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setFormSets: (
      formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
    ) => dispatch(rootReducer.setFormSets({ formSets })),
    setSelectedFormSetId: (selectedFormSetId: string | null) =>
      dispatch(rootReducer.setSelectedFormSetId(selectedFormSetId)),
    setSelectedFormId: (selectedFormId: string | null) =>
      dispatch(rootReducer.setSelectedFormId(selectedFormId)),
    setLoggedInPatient: (patient: api.Patient.Patient) =>
      dispatch(rootReducer.setLoggedInPatient(patient)),
    setAuthenticationCode: (authenticationCode: string) =>
      dispatch(rootReducer.setAuthenticationCode({ authenticationCode })),
    setEhrProviderInformationList: (
      ehrInformation: api.User.EhrProviderInformationPractice[] | null,
    ) => dispatch(rootReducer.setEhrProviderInformationList(ehrInformation)),
    setSelectedProviderId: (providerId: string | null) =>
      dispatch(
        rootReducer.setSelectedProviderId({
          selectedProviderId: providerId,
        }),
      ),
    setProvisionalPatientId: (provisionalPatientId: string) =>
      dispatch(rootReducer.setProvisionalPatientId(provisionalPatientId)),
    setUrlIdentifier: (urlIdentifier: string | null) =>
      dispatch(rootReducer.setUrlIdentifier(urlIdentifier)),
    setDocumentTitle: (documentTitle: string | null) =>
      dispatch(rootReducer.setDocumentTitle(documentTitle)),
    setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) =>
      dispatch(rootReducer.setOnCaptchaSuccess(onCaptchaSuccess)),
    setIsProvisionalPatientReconciled: (
      isProvisionalPatientReconciled: boolean,
    ) =>
      dispatch(
        rootReducer.setIsProvisionalPatientReconciled(
          isProvisionalPatientReconciled,
        ),
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Schedule);
